'use client';

import { motion } from 'framer-motion';
import { Search, FileText, Zap, BarChart3 } from 'lucide-react';
import { EASE_OUT, EASE_CUSTOM } from '@/lib/animations'

const steps = [
    {
        number: '01',
        icon: Search,
        title: 'Diagnóstico profundo',
        desc: '40+ preguntas específicas de tu sector. Analizamos cómo trabajas, qué herramientas usas y dónde pierdes tiempo y dinero sin darte cuenta.',
        color: 'indigo',
    },
    {
        number: '02',
        icon: FileText,
        title: 'Informe + propuesta en 48h',
        desc: 'Recibes un informe con los problemas reales detectados y una propuesta clara: qué cambiar, en qué orden, cuánto cuesta y qué resultados esperar. Por email, vídeo explicativo en WhatsApp o videollamada — tú eliges el formato.',
        color: 'blue',
    },
    {
        number: '03',
        icon: Zap,
        title: 'Implementación por fases',
        desc: 'Nosotros mismos lo implementamos. Ves avance cada semana. Sin intermediarios, sin subcontratas, sin desaparecer 3 meses.',
        color: 'sky',
    },
    {
        number: '04',
        icon: BarChart3,
        title: 'Seguimiento + optimización',
        desc: 'Medimos los resultados reales. Si algo no funciona como esperábamos, lo ajustamos. Tu asesor sigue disponible.',
        color: 'violet',
    },
];

const colorMap = {
    indigo: {
        bg: 'bg-indigo-50',
        border: 'border-indigo-100',
        icon: 'text-indigo-600',
        badge: 'bg-indigo-100 text-indigo-600',
        connector: 'bg-indigo-200',
    },
    blue: {
        bg: 'bg-blue-50',
        border: 'border-blue-100',
        icon: 'text-blue-600',
        badge: 'bg-blue-100 text-blue-600',
        connector: 'bg-blue-200',
    },
    sky: {
        bg: 'bg-sky-50',
        border: 'border-sky-100',
        icon: 'text-sky-600',
        badge: 'bg-sky-100 text-sky-600',
        connector: 'bg-sky-200',
    },
    violet: {
        bg: 'bg-violet-50',
        border: 'border-violet-100',
        icon: 'text-violet-600',
        badge: 'bg-violet-100 text-violet-600',
        connector: 'bg-violet-200',
    },
};

export default function Proceso() {
    return (
        <section id="proceso" className="py-20 md:py-32 relative z-10">
            <div className="max-w-7xl mx-auto px-6 lg:px-8">

                {/* Header */}
                <div className="text-center mb-16">
                    <div className="inline-block mb-4 text-xs font-mono tracking-[0.3em] text-slate-400 uppercase">
                        Cómo funciona
                    </div>
                    <h2 className="text-2xl sm:text-4xl md:text-5xl font-black tracking-tight text-slate-900 mb-4">
                        Primero entendemos.
                        <br />
                        <span className="text-slate-400 font-light">Después actuamos.</span>
                    </h2>
                    <p className="text-lg text-slate-500 font-light max-w-xl mx-auto">
                        No proponemos nada sin antes analizar tu negocio. Precio claro antes de empezar. Resultados medibles.
                    </p>
                </div>

                {/* Steps grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 relative">

                    {/* Connector line — desktop only */}
                    <div className="hidden lg:block absolute top-8 left-[12.5%] right-[12.5%] h-px bg-gradient-to-r from-indigo-200 via-blue-200 via-sky-200 to-violet-200 z-0" />

                    {steps.map((step, i) => {
                        const c = colorMap[step.color as keyof typeof colorMap];
                        const Icon = step.icon;
                        return (
                            <motion.div
                                key={step.number}
                                initial={{ opacity: 0, y: 24 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: '-60px' }}
                                transition={{ duration: 0.7, delay: i * 0.1, ease: EASE_CUSTOM }}
                                className="relative z-10 glass-card rounded-2xl p-6 flex flex-col gap-4"
                            >
                                {/* Step number + icon */}
                                <div className="flex items-center justify-between">
                                    <div className={`w-12 h-12 rounded-xl ${c.bg} border ${c.border} flex items-center justify-center`}>
                                        <Icon className={`w-5 h-5 ${c.icon}`} />
                                    </div>
                                    <span className={`text-xs font-mono font-bold px-2 py-1 rounded-lg ${c.badge}`}>
                                        {step.number}
                                    </span>
                                </div>

                                <div>
                                    <h3 className="font-bold text-slate-900 text-base mb-2">{step.title}</h3>
                                    <p className="text-sm text-slate-500 font-light leading-relaxed">{step.desc}</p>
                                </div>
                            </motion.div>
                        );
                    })}
                </div>

                {/* Bottom CTA */}
                <motion.div
                    initial={{ opacity: 0, y: 12 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.7, delay: 0.4 }}
                    className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-4"
                >
                    <a
                        href="#contacto"
                        className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-blue-600 text-white text-sm font-semibold shadow-[0_8px_24px_rgba(37,99,235,0.3)] hover:bg-blue-700 transition-all duration-200 active:scale-95"
                    >
                        Empezar con el diagnóstico →
                    </a>
                    <span className="text-xs text-slate-400 font-mono uppercase tracking-widest">
                        Gratuito · Sin compromiso
                    </span>
                </motion.div>
            </div>
        </section>
    );
}
